/**
 * Shared Axios instance for all API calls.
 *
 * Provides:
 *  - Automatic Authorization header injection from localStorage
 *  - X-CSRF-Token header injection
 *  - withCredentials: true (sends cookies on cross-origin requests)
 *  - 401 → token refresh with request queue to prevent thundering herd
 */

import axios from 'axios';
import { getCSRFToken } from '../utils/csrf';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── Token refresh state ─────────────────────────────────────────────────────

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

// ─── Request interceptor ─────────────────────────────────────────────────────

apiClient.interceptors.request.use((config) => {
  // Primary auth is httpOnly cookies (withCredentials: true).
  // This header injection is a legacy fallback — some older endpoints may check it.
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  const csrfToken = getCSRFToken();
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }

  return config;
});

// ─── Response interceptor ────────────────────────────────────────────────────

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // In dev mock mode there are no real tokens — skip the refresh/redirect cycle
      // entirely and let the calling component handle the empty response gracefully.
      if (import.meta.env.DEV && import.meta.env.VITE_DEV_MOCK_USER) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until the refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Make a direct fetch call to avoid circular interceptor loops
        const refreshResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/api/auth/refresh`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...(getCSRFToken() ? { 'X-CSRF-Token': getCSRFToken() } : {}),
            },
          }
        );

        if (!refreshResponse.ok) throw new Error('Token refresh failed');

        // Cookie is already set by the server response — no need to store a token.
        // The refresh endpoint returns { success: true } with no access_token field.
        await refreshResponse.json();

        processQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem('access_token');
        window.location.href = '/agent/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 402 — Itinerary limit reached. Fire a global event so the UI can respond
    // with a contextual upgrade prompt rather than a generic error message.
    if (error.response?.status === 402) {
      const detail = error.response.data ?? {};
      window.dispatchEvent(
        new CustomEvent('parkpro:itinerary-limit-reached', { detail })
      );
    }

    return Promise.reject(error);
  }
);

export default apiClient;
